import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, AppLink, C, Callout } from "./primitives.js";

export const meta = {
  id: "backups",
  title: "SQLite backups",
  category: "Reference",
  lastUpdated: "2026-06-10",
  description: "Nightly hot-backup of the keywords DB at 02:00 IL. Manual trigger, retention policy, restore steps, where files live.",
  relatedGuides: ["error-log", "spend-dashboard", "troubleshooting"],
  readTimeMin: 4,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        The keywords SQLite DB holds 21 tables — every keyword, brief, article, storyboard, asset, cost row, error log, suggestion, distribution. One corrupt file = the whole pipeline state is gone. The nightly hot-backup is the protection layer.
      </Lead>

      <H2>What runs and when</H2>
      <Ul>
        <Li><strong>Schedule</strong> — 02:00 IL daily by default. Cron: <C>0 2 * * *</C>. Runs BEFORE the other nightly jobs (Content DB sync, PAA mining, GSC) so a fresh backup exists at the start of every day.</Li>
        <Li><strong>Method</strong> — better-sqlite3's <C>db.backup()</C> API. Pages stream to the destination file while the source DB stays writable. No locks, no <C>VACUUM</C>, no <C>WAL checkpoint</C> dance.</Li>
        <Li><strong>Location</strong> — <C>data/backups/keywords-YYYY-MM-DD-HHmmss.db</C>. Local filesystem only; offsite S3 copy lands once the AWS baseline ships.</Li>
        <Li><strong>Retention</strong> — last 30 most-recent files. Older files pruned automatically after each backup. Override via <C>BACKUP_RETAIN_COUNT</C>.</Li>
        <Li><strong>Cost</strong> — $0. Disk space ~700KB per backup at current scale; pruning keeps total under ~25MB.</Li>
      </Ul>

      <H2>Operator surface</H2>
      <Ul>
        <Li><strong>Spend page</strong> — <AppLink to="spend">#/spend</AppLink> has a "SQLite backups" section at the bottom showing count, total size, newest age, and a "Back up now" button. A red banner appears if the newest backup is more than 1 day old (which means the cron didn't fire — check the Errors page).</Li>
        <Li><strong>CLI</strong> — <C>node keywords/dist/cli.js backup [action]</C>:
          <ul>
            <li><C>now</C> (default) — run a backup immediately</li>
            <li><C>list</C> — show all backups with sizes + ages</li>
            <li><C>status</C> — summary stats</li>
            <li><C>restore &lt;date-or-filename&gt;</C> — print safe-restore instructions (does NOT auto-restore)</li>
            <li><C>prune</C> — apply retention manually</li>
          </ul>
        </Li>
        <Li><strong>API</strong> — <C>GET /api/admin/backup</C> (status), <C>POST /api/admin/backup/run</C> (trigger), <C>POST /api/admin/backup/prune</C>.</Li>
      </Ul>

      <Callout kind="tip">
        The "Back up now" button is the right reflex before any risky migration or operator-driven bulk delete. Hit it, see the new backup in the list, then proceed.
      </Callout>

      <H2>Restoring</H2>
      <P>
        The CLI deliberately does NOT auto-restore. The ui-server holds an open file handle on the live DB, and silently overwriting that file produces undefined behaviour ranging from "works fine" to "corrupt rows on next write." The CLI prints the safe sequence and you run it:
      </P>
      <ol className="my-3 list-decimal pl-6 text-sm">
        <li>Stop the ui-server (any process holding the DB open).</li>
        <li><C>mv data/keywords/keywords.db data/keywords/keywords.db.before-restore</C></li>
        <li><C>cp data/backups/keywords-YYYY-MM-DD-HHmmss.db data/keywords/keywords.db</C></li>
        <li>Restart the ui-server.</li>
      </ol>
      <P>
        Find the right backup with <C>node keywords/dist/cli.js backup list</C> or by browsing <C>data/backups/</C> directly.
      </P>

      <H2>Failure modes</H2>
      <Ul>
        <Li>
          <strong>Newest backup older than 1 day</strong> — the Spend page shows a warning banner. Cause: the cron didn't fire (ui-server was down) OR the backup failed (disk full, permissions). Check the <AppLink to="errors">Errors page</AppLink> for <C>scheduler.backup</C> entries.
        </Li>
        <Li>
          <strong>Disk pressure</strong> — current scale puts each backup at ~700KB. At 30 retained that's ~21MB. If the DB ever balloons (e.g. 100K keywords) plan for ~50MB+ per backup; either lower <C>BACKUP_RETAIN_COUNT</C> or move <C>BACKUP_DIR</C> to a bigger disk.
        </Li>
        <Li>
          <strong>"backup already in flight"</strong> — manual trigger returned 409 because the cron picked the same moment. Wait 30 seconds and try again.
        </Li>
      </Ul>

      <H2>Future work</H2>
      <Ul>
        <Li><strong>S3 offsite copy</strong> — lands with the AWS baseline. Currently parked. Once shipped, the manager will also push each new backup to an S3 bucket so a laptop-disk failure isn't catastrophic.</Li>
        <Li><strong>Tiered retention</strong> — "30 daily + 12 weekly + 12 monthly" instead of flat 30. Add when real growth requires it.</Li>
        <Li><strong>Integrity check</strong> — periodic <C>PRAGMA integrity_check</C> on the most recent backup to confirm it's restorable. Useful but lower-priority while we're disk-local.</Li>
      </Ul>
    </>
  );
}

export const guide: Guide = { meta, Body };
